import React, { useEffect, useRef, useState } from 'react';

import { jackpotBetAmountsType } from '@/types/jackpotTypes';

import styles from './JackpotBetWidget.module.scss';

export interface JackpotBetWidgetTypeProps {
  jpAmounts: jackpotBetAmountsType[];
  currencySymbol: string;
}

const ANIMATION_TIME = 300;

const JackpotBetWidgetType = ({
  jpAmounts,
  currencySymbol,
}: JackpotBetWidgetTypeProps) => {
  const convertToLocalString = (amount: number) =>
    amount.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const jpBetAmounts = jpAmounts.map((jpAmount: jackpotBetAmountsType) => ({
    type: jpAmount.type,
    value: `${currencySymbol}${
      convertToLocalString(jpAmount.amount).split('.')[0]
    }`,
  }));
  const [animate, setAnimate] = useState(false);
  const [currentName, setCurrentName] = useState(jpBetAmounts[0].type);
  const [currentValue, setCurrentValue] = useState(jpBetAmounts[0].value);
  const [currentDecimalValue, setCurrentDecimalValue] = useState(
    convertToLocalString(jpAmounts[0].amount).split('.')[1] || '00'
  );
  const decimalValue = animate ? `.00` : `.${currentDecimalValue}`;

  const index = useRef(0);
  const currency = useRef(currencySymbol);

  useEffect(() => {
    if (currency.current !== currencySymbol) {
      setCurrentName(jpBetAmounts[index.current].type);
      setCurrentValue(jpBetAmounts[index.current].value);
      setCurrentDecimalValue(
        convertToLocalString(jpAmounts[index.current].amount).split('.')[1] ||
          '00'
      );
      currency.current = currencySymbol;
    }

    const interval = setInterval(() => {
      setAnimate(true);

      const newIndex = (index.current + 1) % jpBetAmounts.length;

      setTimeout(() => {
        setCurrentName(jpBetAmounts[newIndex].type);
        setCurrentValue(jpBetAmounts[newIndex].value);
        setCurrentDecimalValue(
          convertToLocalString(jpAmounts[newIndex].amount).split('.')[1] || '00'
        );
        index.current = newIndex;
        setAnimate(false);
      }, ANIMATION_TIME);
    }, 4000);

    return () => clearInterval(interval);
  }, [jpAmounts, jpBetAmounts, currencySymbol]);

  return (
    <div
      className={[
        styles.jackpotType,
        animate ? null : styles.jackpotTypeShadow,
      ].join(' ')}
      data-testid="jackpot-bet-types"
    >
      <div
        className={[
          styles.jackpotName,
          animate ? styles.hidden : styles.visible,
        ].join(' ')}
      >
        {currentName}
      </div>
      <div className={styles.jackpotValueContainer}>
        <div className={styles.jackpotValueAnimated}>
          <span
            className={[
              styles.jackpotValue,
              animate ? styles.exit : styles.enter,
            ].join(' ')}
          >
            {' '}
            {currentValue}
          </span>
          <span
            className={[
              styles.jackpotValueZeroAnimation,
              animate ? styles.visible : '',
            ].join(' ')}
          >
            00
          </span>
        </div>
        <div>
          <span className={styles.jackpotValueDecimals}>{decimalValue}</span>
        </div>
      </div>
    </div>
  );
};

export default JackpotBetWidgetType;
